import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, CheckCircle2, Loader2, ShieldCheck, UploadCloud } from "lucide-react";
import { trackProductEvent } from "@/lib/productMetrics";
import { resolveMediaUrl } from "@/lib/mediaUrl";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MediaImportPage = () => {
  const { getAuthHeaders } = useAuth();
  const [usageSummary, setUsageSummary] = useState(null);
  const [permissionConfirmed, setPermissionConfirmed] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [directUrl, setDirectUrl] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [isImportingUrl, setIsImportingUrl] = useState(false);
  const [task, setTask] = useState(null);
  const [importDoc, setImportDoc] = useState(null);
  const [createAnalysis, setCreateAnalysis] = useState(true);
  const [isAssociating, setIsAssociating] = useState(false);

  const canStart = permissionConfirmed;
  const done = task?.status === "completed";
  const failed = task?.status === "failed";
  const active = task?.status === "pending" || task?.status === "processing";

  const fetchUsageSummary = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/usage`, {
        headers: getAuthHeaders(),
      });
      setUsageSummary(response.data);
    } catch (error) {
      console.error("Error fetching media import usage:", error);
    }
  }, [getAuthHeaders]);

  const statusLabel = useMemo(() => {
    if (!task) return null;
    if (task.status === "completed") return "Completado";
    if (task.status === "failed") return "Fallido";
    if (task.status === "processing") return "Procesando";
    return "Pendiente";
  }, [task]);

  const refreshImport = useCallback(async (importId) => {
    const response = await axios.get(`${API_URL}/media-import/${importId}`, {
      headers: getAuthHeaders(),
    });
    setImportDoc(response.data);
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchUsageSummary();
  }, [fetchUsageSummary]);

  const startUpload = async () => {
    if (!selectedFile) {
      toast.error("Selecciona un archivo local");
      return;
    }
    if (!canStart) {
      toast.error("Debes confirmar permiso de uso");
      return;
    }

    const data = new FormData();
    data.append("file", selectedFile);
    data.append("permission_confirmed", "true");

    setIsUploading(true);
    try {
      const response = await axios.post(`${API_URL}/media-import/upload`, data, {
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "multipart/form-data",
        },
      });
      setTask({
        task_id: response.data.task_id,
        import_id: response.data.import_id,
        status: response.data.status,
        progress: 0,
      });
      setImportDoc(null);
      await fetchUsageSummary();
      toast.success("Importación iniciada");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al importar archivo");
    } finally {
      setIsUploading(false);
    }
  };

  const startAuthorizedUrlImport = async () => {
    if (!directUrl.trim()) {
      toast.error("Ingresa una URL directa autorizada");
      return;
    }
    if (!canStart) {
      toast.error("Debes confirmar permiso de uso");
      return;
    }

    setIsImportingUrl(true);
    try {
      const response = await axios.post(
        `${API_URL}/media-import/import-url`,
        {
          url: directUrl.trim(),
          permission_confirmed: true,
        },
        {
          headers: getAuthHeaders(),
        }
      );
      setTask({
        task_id: response.data.task_id,
        import_id: response.data.import_id,
        status: response.data.status,
        progress: 0,
      });
      setImportDoc(null);
      await fetchUsageSummary();
      toast.success("Importación por URL iniciada");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al importar URL");
    } finally {
      setIsImportingUrl(false);
    }
  };

  const associateImport = async () => {
    if (!importDoc?.id) return;
    setIsAssociating(true);
    try {
      const response = await axios.post(
        `${API_URL}/media-import/${importDoc.id}/associate-song-source`,
        {
          song_source_id: null,
          create_analysis: createAnalysis,
        },
        { headers: getAuthHeaders() }
      );
      setImportDoc(response.data);
      if (response.data.associated_analysis_id) {
        await fetchUsageSummary();
        toast.success("Importación asociada y análisis creado");
      } else {
        toast.success("Importación asociada");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "No fue posible asociar la importación");
    } finally {
      setIsAssociating(false);
    }
  };

  useEffect(() => {
    if (!task?.task_id) return;
    if (task.status === "completed" || task.status === "failed") return;

    let cancelled = false;
    const timer = setInterval(async () => {
      try {
        const response = await axios.get(`${API_URL}/media-import/task/${task.task_id}`, {
          headers: getAuthHeaders(),
        });
        if (cancelled) return;
        setTask(response.data);
        if (response.data.status === "completed") {
          await refreshImport(response.data.import_id);
          toast.success("Importación completada");
        }
        if (response.data.status === "failed") {
          toast.error(response.data.error || "La importación falló");
        }
      } catch (error) {
        toast.error(error.response?.data?.detail || "Error consultando estado de tarea");
      }
    }, 2000);

    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [task?.task_id, task?.status, getAuthHeaders, refreshImport]);

  return (
    <div className="min-h-screen bg-[#09090b]">
      <header className="sticky top-0 z-50 glass-panel">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center gap-4">
          <Link to="/dashboard" className="text-gray-400 hover:text-white transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <h1 className="text-white font-semibold">Importación Multimedia Autorizada</h1>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8 space-y-6">
        {usageSummary && (
          <Card className="bg-[#18181b] border-white/10">
            <CardContent className="pt-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <p className="text-white font-semibold">
                  Importaciones disponibles: {usageSummary.remaining.media_import}/{usageSummary.limits.media_import}
                </p>
                <p className="text-sm text-gray-400">
                  Plan {usageSummary.plan === "pro" ? "Pro" : "Free"} · periodo {usageSummary.period_month}. Solo archivos propios o URLs directas autorizadas.
                </p>
              </div>
              {usageSummary.plan !== "pro" && (
                <Link
                  to="/pricing"
                  onClick={() => trackProductEvent("pro_cta_clicked", { location: "media_import_usage_card" }, getAuthHeaders)}
                >
                  <Button className="bg-primary hover:bg-primary/90">Upgrade a PRO</Button>
                </Link>
              )}
            </CardContent>
          </Card>
        )}

        <Card className="bg-[#18181b] border-white/10">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-primary" />
              Confirmación de permiso
            </CardTitle>
          </CardHeader>
          <CardContent>
            <label className="flex items-start gap-3 text-sm text-gray-300 cursor-pointer">
              <input
                type="checkbox"
                checked={permissionConfirmed}
                onChange={(e) => setPermissionConfirmed(e.target.checked)}
                className="mt-1"
              />
              <span>
                Confirmo que tengo derecho o autorización para usar este archivo multimedia.
                No se permiten URLs de YouTube ni plataformas equivalentes para descarga.
              </span>
            </label>
          </CardContent>
        </Card>

        <div className="grid lg:grid-cols-2 gap-6">
          <Card className="bg-[#18181b] border-white/10">
            <CardHeader>
              <CardTitle className="text-white">Upload local</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Input
                type="file"
                accept=".mp3,.wav,.flac,.m4a,.aac,.ogg,.mp4,.mov"
                onChange={(event) => setSelectedFile(event.target.files?.[0] || null)}
                className="bg-black/30 border-white/10 text-white"
              />
              <Button
                onClick={startUpload}
                disabled={!canStart || !selectedFile || isUploading}
                className="bg-primary hover:bg-primary/90 gap-2"
              >
                {isUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />}
                Importar archivo
              </Button>
            </CardContent>
          </Card>

          <Card className="bg-[#18181b] border-white/10">
            <CardHeader>
              <CardTitle className="text-white">URL directa autorizada</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Input
                value={directUrl}
                onChange={(event) => setDirectUrl(event.target.value)}
                placeholder="https://dominio.com/audio.wav"
                className="bg-black/30 border-white/10 text-white"
              />
              <Button
                onClick={startAuthorizedUrlImport}
                disabled={!canStart || !directUrl.trim() || isImportingUrl}
                className="bg-primary hover:bg-primary/90 gap-2"
              >
                {isImportingUrl ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />}
                Importar desde URL
              </Button>
            </CardContent>
          </Card>
        </div>

        {task && (
          <Card className="bg-[#18181b] border-white/10">
            <CardHeader>
              <CardTitle className="text-white">Estado de tarea</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3">
                <Badge className="bg-primary/20 text-primary border border-primary/30">{statusLabel}</Badge>
                <span className="text-xs text-gray-500">Importacion segura en curso</span>
              </div>
              <Progress value={task.progress || 0} className="h-2" />
              {task.error && <p className="text-sm text-red-400">{task.error}</p>}
              {active && <p className="text-sm text-gray-400">Procesando importación, espera unos segundos...</p>}
            </CardContent>
          </Card>
        )}

        {done && importDoc && (
          <Card className="bg-[#18181b] border-white/10">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-500" />
                Resultado importado
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <p className="text-gray-300">Archivo: <span className="text-white">{importDoc.filename}</span></p>
              <p className="text-gray-300 break-all">Media URL: <a className="text-primary hover:underline" href={resolveMediaUrl(importDoc.media_url)} target="_blank" rel="noreferrer">{resolveMediaUrl(importDoc.media_url)}</a></p>
              <p className="text-gray-400">Tipo: {importDoc.content_type || "N/A"} | Extensión: {importDoc.extension || "N/A"}</p>

              <div className="pt-2">
                <label className="flex items-center gap-2 text-gray-300">
                  <input
                    type="checkbox"
                    checked={createAnalysis}
                    onChange={(event) => setCreateAnalysis(event.target.checked)}
                  />
                  Crear analisis musical desde este audio
                </label>
              </div>

              <div className="flex flex-wrap gap-3">
                <Button onClick={associateImport} disabled={isAssociating} className="bg-primary hover:bg-primary/90">
                  {isAssociating ? "Preparando..." : "Continuar con analisis"}
                </Button>
                {importDoc.associated_analysis_id && (
                  <Link to={`/analysis/${importDoc.associated_analysis_id}`}>
                    <Button variant="outline" className="border-white/10 text-gray-300">
                      Ir al análisis
                    </Button>
                  </Link>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {failed && (
          <Card className="bg-[#18181b] border-red-500/30">
            <CardContent className="py-6">
              <p className="text-red-400 text-sm">La importación falló. Revisa el mensaje de error y vuelve a intentar.</p>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default MediaImportPage;
